import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, LockKeyhole } from "lucide-react";
import { BrandLogo } from "@/components/Logo";

export const APP_URL = "https://app.prodigyincorporated.it";

export const AreaLink = ({ testId, className = "", onClick, tabIndex }) => (
  <a
    href={APP_URL}
    target="_blank"
    rel="noopener noreferrer"
    onClick={onClick}
    tabIndex={tabIndex}
    className={`area-link cta cta-ghost glass group/area inline-flex min-h-[44px] items-center justify-center gap-2.5 rounded-full border px-5 py-2.5 text-sm font-semibold text-white/85 ${className}`}
    data-testid={testId}
  >
    <LockKeyhole className="h-4 w-4 text-cyan" strokeWidth={1.75} />
    Accedi all'area riservata
    <ArrowUpRight className="h-4 w-4 text-cyan transition-transform duration-300 group-hover/area:translate-x-0.5 group-hover/area:-translate-y-0.5" />
  </a>
);

const LINKS = [
  { to: "/", label: "Home", id: "home", end: true },
  { to: "/aziende", label: "Per le Aziende", id: "aziende" },
  { to: "/istituti", label: "Per gli Istituti", id: "istituti" },
  { to: "/candidati", label: "Per i Candidati", id: "candidati" },
];

const linkClass = ({ isActive }) =>
  `nav-link block whitespace-nowrap rounded-full px-2.5 xl:px-3.5 py-2 text-[13px] xl:text-sm font-medium ${isActive ? "text-cyan" : "text-titanium"}`;

const CvCta = ({ testId, className = "" }) => (
  <a
    href={APP_URL}
    target="_blank"
    rel="noopener noreferrer"
    className={`cta cta-glow cta-nav inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border px-4 xl:px-5 py-2.5 text-[13px] xl:text-sm font-semibold text-white ${className}`}
    data-testid={testId}
  >
    Costruisci il tuo CV
    <ArrowUpRight className="h-4 w-4 text-cyan" />
  </a>
);

const MobilePanel = ({ open, onNav, onClose }) => (
  <div
    className={`lg:hidden glass mx-auto max-w-5xl mt-3 rounded-[24px] p-4 origin-top transition-[opacity,transform] duration-300 ${
      open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-2 pointer-events-none invisible"
    }`}
    style={{ background: "rgba(8,8,10,0.92)" }}
    aria-hidden={!open}
    data-testid="nav-mobile-panel"
  >
    <ul className="flex flex-col">
      {LINKS.map((l) => (
        <li key={l.id}>
          <NavLink
            to={l.to}
            end={l.end}
            onClick={() => onNav(l.to)}
            tabIndex={open ? 0 : -1}
            className={({ isActive }) =>
              `nav-link block rounded-2xl px-4 py-4 text-lg font-semibold tracking-[-0.01em] border-b border-[#16171a] ${isActive ? "text-cyan" : "text-white"}`
            }
            data-testid={`nav-mobile-link-${l.id}`}
          >
            {l.label}
          </NavLink>
        </li>
      ))}
    </ul>
    <CvCta testId="nav-mobile-cv-cta" className="mt-4 w-full py-3.5" />
    <AreaLink testId="nav-mobile-area-link" className="mt-3 w-full py-3.5" onClick={onClose} tabIndex={open ? 0 : -1} />
  </div>
);

export const SiteNav = () => {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onNav = (to) => {
    setOpen(false);
    if (to === pathname) window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <header className="fixed top-4 inset-x-0 z-50 px-4 pointer-events-none" data-testid="navbar">
      <nav className="glass pointer-events-auto mx-auto max-w-5xl rounded-full flex items-center justify-between gap-3 pl-5 pr-2 py-2">
        <NavLink to="/" onClick={() => onNav("/")} className="shrink-0" data-testid="navbar-logo" aria-label="Prodigy, torna alla home">
          <BrandLogo className="header-logo" testId="header-logo" />
        </NavLink>
        <div className="hidden lg:flex items-center gap-1 xl:gap-2">
          <ul className="flex items-center">
            {LINKS.map((l) => (
              <li key={l.id}>
                <NavLink to={l.to} end={l.end} onClick={() => onNav(l.to)} className={linkClass} data-testid={`nav-link-${l.id}`}>
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
          <CvCta testId="nav-cv-cta" className="ml-1" />
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden h-11 w-11 rounded-full border border-[#2a2d33] flex items-center justify-center text-white"
          aria-label={open ? "Chiudi menu" : "Apri menu"}
          aria-expanded={open}
          data-testid="nav-menu-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>
      <MobilePanel open={open} onNav={onNav} onClose={() => setOpen(false)} />
    </header>
  );
};
